import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createRegisterSchema, RegisterFormValues } from '@/utils/validations/register.schema';
import InputField from '@/components/common/InputField';
import PhoneInput, { PhoneData } from '@/components/common/PhoneInput';
import OtpTimer from '@/components/common/OtpTimer';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import { BsFillPersonFill } from 'react-icons/bs';
import { BiSolidLock } from 'react-icons/bi';
import { MdShield } from 'react-icons/md';
import { FaGift } from 'react-icons/fa';
import { useRegisterMutation, useSendOtpMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks';
import { useOtpTimer } from '@/hooks/useOtpTimer';
import { setToken } from '@/store/slices/authSlice';
import { ApiError } from '@/types/error';
import { getApiErrorMessage, isNetworkError, NETWORK_SLOW_MESSAGE } from '@/utils/apiError';
import { Loader } from '@/components/common/Loader';
import { toast } from 'sonner';

const initialPhone: PhoneData = { countryCode: '', phoneNumber: '', country: '', fullNumber: '' };

const getErrorMessage = getApiErrorMessage;

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [phone, setPhone] = useState<PhoneData>(initialPhone);
  const [searchParams] = useSearchParams();

  const { timeLeft, active, start, reset: resetTimer } = useOtpTimer(120);

  const [sendOtp, { isLoading: otpLoading }] = useSendOtpMutation();
  const [registerMutation, { isLoading, error: registerApiError }] = useRegisterMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    trigger,
    resetField,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(createRegisterSchema()),
    defaultValues: {
      firstname: '',
      lastname: '',
      username: '',
      phone: '',
      otp: '',
      password: '',
      confirmPassword: '',
      referralCode: searchParams.get('ref') || '',
    },
    mode: 'onChange',
  });

  const handleResetOtpField = () => {
    setShowOtpField(false);
    resetTimer();
    resetField('otp');
  };

  const handleSendOtp = async () => {
    const isValid = await trigger('phone');
    if (!isValid) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    try {
      await sendOtp({
        mobile: phone.fullNumber,
        country: phone.country || 'BD',
        type: 'register',
      }).unwrap();

      toast.success(`OTP sent to ${phone.fullNumber}`);
      setShowOtpField(true);
      start();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send OTP'));
    }
  };

  // Pressing SignUp before an OTP was requested: instead of a dead end, send
  // the OTP now so the player only has to type the code.
  const onInvalidSubmit = async () => {
    if (!showOtpField) await handleSendOtp();
  };

  const onSubmit = async (data: RegisterFormValues) => {
    try {
      const payload = {
        firstname: data.firstname || undefined,
        lastname: data.lastname || undefined,
        username: data.username,
        dialCode: phone.countryCode || undefined,
        mobile: phone.fullNumber,
        country: phone.country || 'BD',
        password: data.password,
        referralCode: data.referralCode || undefined,
        mobileVerificationCode: data.otp,
      };

      const res = await registerMutation(payload).unwrap();

      if (res?.accessToken) {
        dispatch(setToken(res.accessToken));
        navigate('/', { replace: true });
      }
    } catch (error) {
      // handled via registerApiError below
    }
  };

  const errorMessage =
    errors.username?.message ||
    errors.phone?.message ||
    errors.otp?.message ||
    errors.password?.message ||
    errors.confirmPassword?.message ||
    (isNetworkError(registerApiError)
      ? NETWORK_SLOW_MESSAGE
      : (registerApiError as ApiError | undefined)?.data?.message);

  return (
    <div
      className="flex flex-col relative h-full !bg-no-repeat !bg-cover !bg-center"
      style={{ background: `url('/images/login-bg.webp')` }}
    >
      {isLoading && (
        <Loader className={'h-[100dvh] flex items-center justify-center bg-black/50'} />
      )}
      <div>
        {/* Back Button */}
        <Link
          to={'/'}
          className="ml-2.5 mt-2.5 inline-flex size-9 items-center justify-center rounded-full bg-white"
        >
          <i className={'icon-arrow-down text-lg rotate-90 text-black'} />
        </Link>

        {/* Logo */}
        <div className="flex justify-center px-8 pb-2 pt-12">
          <img src="/images/logo-9winbet-v2.webp" alt="Velki" className="w-[18rem]" />
          {/* <img src="/images/logo-black.webp" alt="Velki" className="w-[14.5rem]" /> */}
        </div>
      </div>

      {/* Form Card */}
      <div className="flex flex-col">
        {/* Banner */}
        {/* <div className="w-[96%] mx-auto">
          <img src="/images/login-banner.webp" alt="banner" className="w-full" />
        </div> */}

        <div className="py-7 bg-white rounded-t-[1rem]">
          <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className={'w-5/6 mx-auto'}>
            <h1 className="mb-6 text-center text-[1.875rem] font-normal">SIGNUP</h1>

            <div className="space-y-5">
              <InputField
                control={control}
                name="username"
                label="Username"
                startIcon={<BsFillPersonFill className={'text-[1.8rem]'} />}
              />

              {/* Mobile + Send OTP */}
              <div>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <div className="flex gap-2">
                      <PhoneInput
                        value={field.value}
                        disabled={active}
                        defaultCountry="BD"
                        onChange={(data) => {
                          setPhone(data);
                          field.onChange(data.fullNumber);
                        }}
                      />

                      <button
                        type="button"
                        onClick={active ? handleResetOtpField : handleSendOtp}
                        disabled={otpLoading}
                        className="h-[3.125rem] shrink-0 rounded-lg bg-primary px-3 text-[0.875rem] font-bold text-black disabled:opacity-60"
                      >
                        {otpLoading ? '...' : active ? 'Change?' : 'Send OTP'}
                      </button>
                    </div>
                  )}
                />
              </div>

              {showOtpField && (
                <div>
                  <div className="mb-1 flex justify-end">
                    <OtpTimer timeLeft={timeLeft} active={active} onResend={handleSendOtp} />
                  </div>
                  <InputField
                    control={control}
                    name="otp"
                    label="OTP"
                    type="text"
                    startIcon={<MdShield className={'text-[1.8rem]'} />}
                  />
                </div>
              )}

              <InputField
                control={control}
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                startIcon={<BiSolidLock className={'text-[1.8rem]'} />}
                endIcon={
                  <button
                    className="text-[1.625rem]"
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <IoMdEye /> : <IoMdEyeOff />}
                  </button>
                }
              />

              <InputField
                control={control}
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                startIcon={<BiSolidLock className={'text-[1.8rem]'} />}
                endIcon={
                  <button
                    className="text-[1.625rem]"
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <IoMdEye /> : <IoMdEyeOff />}
                  </button>
                }
              />

              <InputField
                control={control}
                name="referralCode"
                label="Referral Code (optional)"
                startIcon={<FaGift className={'text-[1.5rem]'} />}
              />

              {errorMessage && <p className="text-red-600 text-[0.938rem]">{errorMessage}</p>}

              <button
                type="submit"
                disabled={isLoading || otpLoading}
                className="bg-primary p-3 rounded flex justify-center items-center disabled:bg-black-300 disabled:text-black-600 disabled:cursor-not-allowed w-52 h-[3.125rem] mx-auto text-[1.063rem] font-extrabold"
              >
                SignUp
              </button>

              <p className="text-center text-[0.938rem]">
                Already have an account?{' '}
                <Link to="/login" className="font-bold underline">
                  Login
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
